import { useState, useEffect, useCallback } from 'react';
import { C } from '../../lib/tokens.js';
import { t } from '../../lib/data.js';
import { apiFetch } from '../../lib/api.js';
import { useToast } from '../../lib/ToastContext.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { SectionCard } from '../../components/ui/SectionCard.jsx';
import { Modal } from '../../components/ui/Modal.jsx';

// Personal API tokens (WCMS_ApiToken) for scripts and integrations, e.g. the support tools of
// the actfact-ad MCP server. A new token is shown once; the server only keeps its hash.

const EXPIRY_OPTIONS = [
  { days: 30, label: 'account.tokens.expiry.30' },
  { days: 90, label: 'account.tokens.expiry.90' },
  { days: 365, label: 'account.tokens.expiry.365' },
  { days: null, label: 'account.tokens.expiry.never' },
];

// What a token may do besides reading; the server checks them (ScopeFilter) and only grants these.
const SCOPES = [
  { id: 'requests:write', label: 'account.tokens.scope.requests_write', hint: 'account.tokens.scope.requests_write.hint' },
  { id: 'requests:manage', label: 'account.tokens.scope.requests_manage', hint: 'account.tokens.scope.requests_manage.hint' },
  { id: 'timesheets:write', label: 'account.tokens.scope.timesheets_write', hint: 'account.tokens.scope.timesheets_write.hint' },
];

function scopeLabel(id) {
  if (id === 'read') return t('account.tokens.scope.read');
  const scope = SCOPES.find(s => s.id === id);
  return scope ? t(scope.label) : id;
}

const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13, border: `1px solid ${C.mercury}`, borderRadius: 4,
  background: C.platinum, color: C.fg, fontFamily: 'Roboto', boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 500, color: C.iron, marginBottom: 4,
  textTransform: 'uppercase', letterSpacing: '0.04em',
};

function formatDate(ms) {
  return ms ? new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null;
}

function StatusChip({ status }) {
  const styles = {
    active: { background: '#E3F4E8', color: '#1E7B3A' },
    expired: { background: C.platinum, color: C.iron },
    revoked: { background: '#FBE4E7', color: C.rust },
  };
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 99,
      ...(styles[status] ?? styles.expired),
    }}>
      {t(`account.tokens.status.${status}`)}
    </span>
  );
}

function TokenRow({ token, onRevoke }) {
  const created = formatDate(token.created_at);
  const expires = formatDate(token.expires_at);
  const lastUsed = formatDate(token.last_used_at);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${C.mercury}` }}>
      <span className="material-icons" style={{ fontSize: 18, color: token.status === 'active' ? C.liquid : C.silver }}>vpn_key</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: C.fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{token.name}</span>
          <StatusChip status={token.status} />
        </div>
        <div style={{ fontSize: 11, color: C.iron, marginTop: 2 }}>
          {t('account.tokens.created')} {created}
          {' · '}{expires ? `${t('account.tokens.expires')} ${expires}` : t('account.tokens.no_expiry')}
          {' · '}{lastUsed ? `${t('account.tokens.last_used')} ${lastUsed}` : t('account.tokens.never_used')}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
          {(token.scopes ?? ['read']).map(scope => (
            <span key={scope} style={{ fontSize: 10, color: C.iron, border: `1px solid ${C.mercury}`, borderRadius: 99, padding: '1px 6px' }}>
              {scopeLabel(scope)}
            </span>
          ))}
        </div>
      </div>
      {token.status === 'active' && (
        <Button kind="ghost" size="sm" icon="block" onClick={() => onRevoke(token)}>{t('account.tokens.revoke')}</Button>
      )}
    </div>
  );
}

function CreateTokenModal({ onClose, onCreated }) {
  const { addToast } = useToast();
  const [name, setName] = useState('');
  const [days, setDays] = useState(90);
  const [scopes, setScopes] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggleScope = (id) => setScopes(list => list.includes(id) ? list.filter(s => s !== id) : [...list, id]);

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    apiFetch('/me/api-tokens', { method: 'POST', body: JSON.stringify({ name: name.trim(), expires_in_days: days, scopes }) })
      .then(onCreated)
      .catch(err => addToast(err.message || t('account.tokens.create_failed')))
      .finally(() => setSaving(false));
  };

  return (
    <Modal onClose={onClose} width={440}>
      <div style={{ fontSize: 15, fontWeight: 500, color: C.fg }}>{t('account.tokens.new')}</div>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={labelStyle}>{t('account.tokens.name')}</label>
          <input autoFocus maxLength={60} value={name} onChange={e => setName(e.target.value)}
            placeholder={t('account.tokens.name.placeholder')} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>{t('account.tokens.expiry')}</label>
          <select value={days ?? ''} onChange={e => setDays(e.target.value === '' ? null : Number(e.target.value))} style={inputStyle}>
            {EXPIRY_OPTIONS.map(o => <option key={o.label} value={o.days ?? ''}>{t(o.label)}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>{t('account.tokens.scopes')}</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: C.silver }}>
              <input type="checkbox" checked disabled style={{ marginTop: 2 }} />
              <span>{t('account.tokens.scope.read')}<div style={{ fontSize: 11 }}>{t('account.tokens.scope.read.hint')}</div></span>
            </label>
            {SCOPES.map(scope => (
              <label key={scope.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: C.fg, cursor: 'pointer' }}>
                <input type="checkbox" checked={scopes.includes(scope.id)} onChange={() => toggleScope(scope.id)} style={{ marginTop: 2 }} />
                <span>{t(scope.label)}<div style={{ fontSize: 11, color: C.iron }}>{t(scope.hint)}</div></span>
              </label>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 12, color: C.iron }}>{t('account.tokens.scope_note')}</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button kind="outline" onClick={onClose}>{t('account.tokens.cancel')}</Button>
          <Button kind="primary" type="submit" icon="add" disabled={saving || !name.trim()}>{t('account.tokens.create')}</Button>
        </div>
      </form>
    </Modal>
  );
}

function NewTokenModal({ created, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(created.token).then(() => setCopied(true));
  };
  return (
    <Modal onClose={onClose} width={520}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="material-icons" style={{ fontSize: 20, color: C.liquid }}>vpn_key</span>
        <div style={{ fontSize: 15, fontWeight: 500, color: C.fg }}>{created.name}</div>
      </div>
      <div style={{ fontSize: 12, color: C.rust }}>{t('account.tokens.copy_now')}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: C.platinum, border: `1px solid ${C.mercury}`, borderRadius: 4 }}>
        <code style={{ flex: 1, minWidth: 0, fontSize: 12, fontFamily: 'Roboto Mono, monospace', wordBreak: 'break-all', color: C.fg }}>{created.token}</code>
        <Button kind="outline" size="sm" icon={copied ? 'check' : 'content_copy'} onClick={copy}>
          {copied ? t('account.tokens.copied') : t('account.tokens.copy')}
        </Button>
      </div>
      <div style={{ fontSize: 12, color: C.iron }}>{t('account.tokens.usage')} <code>Authorization: Bearer &lt;token&gt;</code></div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button kind="primary" onClick={onClose}>{t('account.tokens.done')}</Button>
      </div>
    </Modal>
  );
}

function RevokeModal({ token, onConfirm, onCancel }) {
  return (
    <Modal onClose={onCancel} width={420}>
      <div style={{ fontSize: 15, fontWeight: 500, color: C.fg }}>{t('account.tokens.revoke_title')}</div>
      <div style={{ fontSize: 13, color: C.fg }}>
        <b>{token.name}</b>
      </div>
      <div style={{ fontSize: 12, color: C.rust }}>{t('account.tokens.revoke_note')}</div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button kind="outline" onClick={onCancel}>{t('account.tokens.cancel')}</Button>
        <Button kind="danger" icon="block" onClick={onConfirm}>{t('account.tokens.revoke')}</Button>
      </div>
    </Modal>
  );
}

export function ApiTokensSection() {
  const { addToast } = useToast();
  const [tokens, setTokens] = useState(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(() => {
    apiFetch('/me/api-tokens')
      .then(res => setTokens(res?.data ?? []))
      .catch(() => setTokens([]));
  }, []);

  useEffect(load, [load]);

  const revoke = () => {
    const token = revoking;
    setRevoking(null);
    apiFetch(`/me/api-tokens/${token.id}`, { method: 'DELETE' })
      .then(load)
      .catch(err => addToast(err.message || t('account.tokens.revoke_failed')));
  };

  const active = (tokens ?? []).filter(tk => tk.status === 'active');
  const inactive = (tokens ?? []).filter(tk => tk.status !== 'active');

  return (
    <SectionCard title={t('account.tokens')}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: C.iron }}>{t('account.tokens.intro')}</div>
        <Button kind="primary" size="sm" icon="add" onClick={() => setCreating(true)}>{t('account.tokens.new')}</Button>
      </div>

      {tokens === null ? null : active.length === 0 ? (
        <div style={{ fontSize: 12, color: C.silver, fontStyle: 'italic', padding: '10px 0' }}>{t('account.tokens.none')}</div>
      ) : (
        <div style={{ borderTop: `1px solid ${C.mercury}` }}>
          {active.map(tk => <TokenRow key={tk.id} token={tk} onRevoke={setRevoking} />)}
        </div>
      )}

      {inactive.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button onClick={() => setShowInactive(v => !v)}
            style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: C.iron, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span className="material-icons" style={{ fontSize: 16 }}>{showInactive ? 'expand_less' : 'expand_more'}</span>
            {t('account.tokens.inactive')} ({inactive.length})
          </button>
          {showInactive && inactive.map(tk => <TokenRow key={tk.id} token={tk} onRevoke={setRevoking} />)}
        </div>
      )}

      {creating && (
        <CreateTokenModal
          onClose={() => setCreating(false)}
          onCreated={res => { setCreating(false); setCreated(res); load(); }}
        />
      )}
      {created && <NewTokenModal created={created} onClose={() => setCreated(null)} />}
      {revoking && <RevokeModal token={revoking} onConfirm={revoke} onCancel={() => setRevoking(null)} />}
    </SectionCard>
  );
}
